import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CAMPAIGN, CONSENT_TEXT, QUESTION_OPTIONS, BONUSES } from "@/lib/campaign";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[rgba(255,255,255,0.04)] border border-[var(--line)] text-white text-sm px-4 py-3 rounded-[4px] placeholder:text-[rgba(255,255,255,0.35)] focus:border-[var(--accent)] focus:bg-[rgba(255,255,255,0.06)] transition-colors duration-200 outline-none";

export const RegistrationForm = ({ prefix, showBonuses = false }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    question: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverMsg, setServerMsg] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "consent" ? e.target.checked : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    if (!form.lastName.trim()) e.lastName = "Last name is required.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid 10-digit mobile phone.";
    if (!form.consent) e.consent = "Please check the consent box to complete registration.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setServerMsg("");
    try {
      const attribution = getAttribution();
      await axios.post(`${API}/ghl-lead`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone,
        state: "Florida",
        va_homebuying_question: form.question || null,
        offer_type: CAMPAIGN.offerType,
        consent_boolean: form.consent,
        consent_text: CONSENT_TEXT,
        consent_timestamp: new Date().toISOString(),
        attribution,
        page_url: window.location.href,
      });
      navigate("/thank-you");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setServerMsg(
        typeof detail === "string"
          ? detail
          : "Something went wrong on our end. Your answers are still here — please try submitting again.",
      );
    }
  };

  if (status === "success") return null;

  return (
    <div className="glass-panel rounded-[6px] p-6 md:p-8 text-left">
      <p className="font-mono text-[11px] tracking-[0.28em] text-[var(--accent)] mb-2">WHEN YOU REGISTER</p>
      <h3 className="font-display text-xl md:text-2xl font-medium text-white mb-4">
        Your workshop access includes:
      </h3>
      {showBonuses && (
        <ul className="space-y-2 mb-5">
          {BONUSES.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-[rgba(255,255,255,0.82)]">
              <span aria-hidden="true" className="text-[var(--accent)] mt-[1px]">✓</span>
              {b}
            </li>
          ))}
        </ul>
      )}
      <p className="font-mono text-xs tracking-[0.2em] text-white border-y border-[var(--line)] py-3 mb-6">
        NO ATTENDANCE FEE
      </p>

      <form onSubmit={submit} noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor={`${prefix}-first`} className="sr-only">First name</label>
            <input
              id={`${prefix}-first`}
              data-testid={`${prefix}-first-name-input`}
              className={inputCls}
              placeholder="First name*"
              autoComplete="given-name"
              value={form.firstName}
              onChange={set("firstName")}
              aria-invalid={!!errors.firstName}
            />
            {errors.firstName && <p className="text-xs text-[#ff8a8a] mt-1" role="alert">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor={`${prefix}-last`} className="sr-only">Last name</label>
            <input
              id={`${prefix}-last`}
              data-testid={`${prefix}-last-name-input`}
              className={inputCls}
              placeholder="Last name*"
              autoComplete="family-name"
              value={form.lastName}
              onChange={set("lastName")}
              aria-invalid={!!errors.lastName}
            />
            {errors.lastName && <p className="text-xs text-[#ff8a8a] mt-1" role="alert">{errors.lastName}</p>}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor={`${prefix}-email`} className="sr-only">Email</label>
          <input
            id={`${prefix}-email`}
            data-testid={`${prefix}-email-input`}
            type="email"
            className={inputCls}
            placeholder="Email*"
            autoComplete="email"
            value={form.email}
            onChange={set("email")}
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="text-xs text-[#ff8a8a] mt-1" role="alert">{errors.email}</p>}
        </div>

        <div className="mb-3">
          <label htmlFor={`${prefix}-phone`} className="sr-only">Mobile phone</label>
          <input
            id={`${prefix}-phone`}
            data-testid={`${prefix}-phone-input`}
            type="tel"
            className={inputCls}
            placeholder="Mobile phone*"
            autoComplete="tel"
            value={form.phone}
            onChange={set("phone")}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && <p className="text-xs text-[#ff8a8a] mt-1" role="alert">{errors.phone}</p>}
        </div>

        <div className="mb-3">
          <label htmlFor={`${prefix}-state`} className="font-mono text-[10px] tracking-[0.18em] text-[var(--muted)] block mb-1">
            STATE — Currently designed for Florida properties
          </label>
          <input
            id={`${prefix}-state`}
            data-testid={`${prefix}-state-input`}
            className={`${inputCls} opacity-70 cursor-not-allowed`}
            value="Florida"
            readOnly
            aria-readonly="true"
          />
        </div>

        <div className="mb-4">
          <label htmlFor={`${prefix}-question`} className="sr-only">VA homebuying question (optional)</label>
          <select
            id={`${prefix}-question`}
            data-testid={`${prefix}-question-select`}
            className={`${inputCls} appearance-none ${form.question ? "text-white" : "text-[rgba(255,255,255,0.35)]"}`}
            value={form.question}
            onChange={set("question")}
          >
            <option value="" className="text-black">VA homebuying question (optional)</option>
            {QUESTION_OPTIONS.map((o) => (
              <option key={o} value={o} className="text-black">{o}</option>
            ))}
          </select>
        </div>

        {/* Hidden field: offer_type */}
        <input type="hidden" name="offer_type" value={CAMPAIGN.offerType} />

        {/* [[COMPLIANCE TO REVIEW — TCPA CONSENT COPY]] */}
        <div className="flex items-start gap-3 mb-5">
          <input
            id={`${prefix}-consent`}
            data-testid={`${prefix}-consent-checkbox`}
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            className="mt-[3px] h-4 w-4 shrink-0 cursor-pointer"
            aria-invalid={!!errors.consent}
          />
          <label htmlFor={`${prefix}-consent`} className="text-[11px] leading-relaxed text-[var(--muted)] cursor-pointer">
            By checking this box, I agree to receive recurring informational and marketing calls and text
            messages from Kinsfolk Mortgage and Chaunci Witherspoon, MBA at the number provided, including
            through automated technology. Consent is not a condition of purchase. Message and data rates may
            apply. Frequency varies. Reply STOP to opt out and HELP for help. View the{" "}
            <a href="/privacy-policy" className="underline text-white hover:text-[var(--accent)]">Privacy Policy</a> and{" "}
            <a href="/terms" className="underline text-white hover:text-[var(--accent)]">Terms</a>.
          </label>
        </div>
        {errors.consent && <p className="text-xs text-[#ff8a8a] mb-3" role="alert">{errors.consent}</p>}

        {status === "error" && (
          <div
            data-testid={`${prefix}-error-message`}
            role="alert"
            className="mb-4 px-4 py-3 rounded-[4px] text-sm text-[#ffb0b0]"
            style={{ background: "var(--danger-soft)", border: "1px solid rgba(255,90,90,0.25)" }}
          >
            {serverMsg}
          </div>
        )}

        <button
          type="submit"
          data-testid={`${prefix}-submit-button`}
          disabled={status === "submitting"}
          className="glint w-full bg-[var(--accent)] text-[var(--background-deep)] font-semibold text-sm tracking-[0.12em] py-4 rounded-[4px] hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0 transition-[transform,filter] duration-200 disabled:opacity-60 disabled:cursor-wait"
        >
          {status === "submitting" ? "RESERVING YOUR SEAT…" : "RESERVE MY FREE SEAT"}
        </button>

        <p className="font-mono text-[10px] tracking-[0.06em] leading-relaxed text-[var(--muted)] mt-4 text-center">
          Free educational event · No loan application required · No hard credit pull required to attend
        </p>
      </form>
    </div>
  );
};
